/**
 * Request-access affordance for the no-access screen (finalization WS4).
 * The locked-out member files a request (intent + optional note) without
 * learning anything about the object; while one is pending they see a status
 * chip and may cancel. Approval/denial arrives via the notification bell.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Label, SpecraButton, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Clock, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { classifyRequestAccessOutcome, useAccessRequests, useCancelAccessRequest, useRequestAccess } from '../../pages/Admin/Access/hooks';
export function RequestAccessPanel({ targetObjectType, targetObjectId }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [intent, setIntent] = useState('view');
    const [message, setMessage] = useState('');
    const [alreadyPending, setAlreadyPending] = useState(false);
    const [decidedStatus, setDecidedStatus] = useState('');
    const { data } = useAccessRequests('');
    const request = useRequestAccess();
    const cancel = useCancelAccessRequest();
    // The list is server-scoped to the caller's own filings for non-reviewers;
    // find this target's active thread.
    const mine = (data?.requests ?? []).find((r)=>r.targetObjectType === targetObjectType && r.targetObjectId === targetObjectId && r.status === 'pending');
    if (mine) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "mt-4 flex items-center justify-center gap-2",
            children: [
                /*#__PURE__*/ _jsxs("span", {
                    "data-testid": Testids.deliveryPrograms.requestAccessPendingChip,
                    className: "inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-sm text-amber-900",
                    children: [
                        /*#__PURE__*/ _jsx(Clock, {
                            className: "h-3.5 w-3.5"
                        }),
                        "Requested — pending review"
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "ghost",
                    size: "sm",
                    className: "text-xs text-muted-foreground",
                    "data-testid": Testids.deliveryPrograms.requestAccessCancel,
                    disabled: cancel.isPending,
                    onClick: ()=>cancel.mutate(mine.id),
                    children: "Cancel request"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-4 flex justify-center",
        children: [
            /*#__PURE__*/ _jsxs(SpecraButton, {
                variant: "secondary",
                "data-testid": Testids.deliveryPrograms.requestAccessButton,
                onClick: ()=>setDialogOpen(true),
                children: [
                    /*#__PURE__*/ _jsx(KeyRound, {
                        className: "mr-2 h-4 w-4"
                    }),
                    "Request access"
                ]
            }),
            /*#__PURE__*/ _jsx(Dialog, {
                open: dialogOpen,
                onOpenChange: (open)=>{
                    if (!open) {
                        setAlreadyPending(false);
                        setDecidedStatus('');
                    }
                    setDialogOpen(open);
                },
                children: /*#__PURE__*/ _jsxs(DialogContent, {
                    "data-testid": Testids.deliveryPrograms.requestAccessDialog,
                    children: [
                        /*#__PURE__*/ _jsx(DialogHeader, {
                            children: /*#__PURE__*/ _jsx(DialogTitle, {
                                children: "Request access"
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-4",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            children: "What do you need?"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "grid gap-1.5",
                                            children: [
                                                [
                                                    'view',
                                                    'View',
                                                    'See this content'
                                                ],
                                                [
                                                    'contribute',
                                                    'Contribute',
                                                    'See it and make changes'
                                                ]
                                            ].map(([value, label, meaning])=>/*#__PURE__*/ _jsxs("button", {
                                                    type: "button",
                                                    onClick: ()=>setIntent(value),
                                                    className: `rounded-md border px-3 py-2 text-left text-sm transition-colors ${intent === value ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/40'}`,
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "font-medium",
                                                            children: label
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "ml-2 text-xs text-muted-foreground",
                                                            children: meaning
                                                        })
                                                    ]
                                                }, value))
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            children: "Why? (optional)"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                                            value: message,
                                            placeholder: "A sentence helps the reviewer say yes faster",
                                            onChange: (e)=>setMessage(e.target.value)
                                        })
                                    ]
                                }),
                                alreadyPending && /*#__PURE__*/ _jsx("p", {
                                    className: "text-muted-foreground text-sm",
                                    "data-testid": Testids.deliveryPrograms.requestAccessAlreadyPending,
                                    children: "You already have a pending request for this."
                                }),
                                decidedStatus && /*#__PURE__*/ _jsxs("p", {
                                    className: "text-muted-foreground text-sm",
                                    "data-testid": Testids.deliveryPrograms.requestAccessDecided,
                                    children: [
                                        "Your last request for this was already ",
                                        decidedStatus,
                                        " - this one was not filed again."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-end gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    onClick: ()=>setDialogOpen(false),
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.deliveryPrograms.requestAccessSubmit,
                                    disabled: request.isPending,
                                    onClick: async ()=>{
                                        setAlreadyPending(false);
                                        setDecidedStatus('');
                                        let outcome;
                                        try {
                                            const resp = await request.mutateAsync({
                                                targetObjectType,
                                                targetObjectId,
                                                intent,
                                                message: message.trim()
                                            });
                                            outcome = classifyRequestAccessOutcome(resp);
                                        } catch  {
                                            return; // hook toasts; keep the dialog open
                                        }
                                        if (outcome.kind === 'pending') {
                                            // Converged on the existing pending request - nothing new
                                            // was filed, so the dialog must stay open and say so
                                            // instead of closing silently as if it had succeeded.
                                            setAlreadyPending(true);
                                            return;
                                        }
                                        if (outcome.kind === 'decided') {
                                            // The idempotency-key lookup is now status-scoped to
                                            // pending, so a decided row can no longer come back from a
                                            // fresh filing - this branch is defensive, reachable only
                                            // if a reply comes from a replica that predates that fix
                                            // (a mixed-version rollout window). Kept so the UI never
                                            // misreports a decided request as "already pending" if
                                            // that window is ever hit.
                                            setDecidedStatus(outcome.status);
                                            return;
                                        }
                                        setDialogOpen(false);
                                    },
                                    children: "Send request"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
